import { trackFormSubmitted } from '@/lib/analytics-tracker';
import { useForm } from '@inertiajs/react';
import { buildSubmissionPayload, flattenSubmissionErrors } from './submission-payload';

export type RecipientType = 'to' | 'cc' | 'bcc';

export interface RecipientInput {
    email?: string | null;
    type?: RecipientType | null;
}

export interface SubmitPublicFormOptions {
    formId?: string | null;
    pageId?: string;
    formData: Record<string, unknown>;
    recipients?: RecipientInput[];
    subject?: string | null;
    replyTo?: string | null;
    honeypot?: string | null;
    onSuccess?: () => void;
    onError?: (errors: Record<string, string>) => void;
}

interface SubmissionTransportPayload {
    form_id: string | null;
    page_id: string;
    form_data: Record<string, unknown>;
    recipients: Array<{ email: string; type: RecipientType }>;
    subject?: string;
    reply_to?: string;
    _hp_field?: string;
}

export function usePublicFormSubmission() {
    const form = useForm({
        form_id: null,
        page_id: '',
        form_data: {},
        recipients: [],
    });

    const submitForm = async (options: SubmitPublicFormOptions) => {
        const payload = buildSubmissionPayload(options) as SubmissionTransportPayload;

        form.transform(() => payload);

        await form.post('/form-submissions', {
            preserveScroll: true,
            onSuccess: () => {
                // Fire form_submitted analytics event before the caller's success
                // handler so consumer state changes (success screen, redirect)
                // don't unmount us mid-flight. trackFormSubmitted is fire-and-
                // forget — never awaited, never blocks the UX.
                if (options.formId) {
                    trackFormSubmitted(options.formId, {
                        // Subject doubles as a human-readable label for the
                        // funnel/dashboard surfaces. Most forms set it; falls
                        // through silently when absent.
                        ...(options.subject ? { form_label: options.subject } : {}),
                    });
                }
                options.onSuccess?.();
            },
            onError: (errors: Record<string, string | string[] | undefined>) => {
                options.onError?.(flattenSubmissionErrors(errors));
            },
        });
    };

    return {
        submitForm,
        processing: form.processing,
        clearErrors: form.clearErrors,
        flattenErrors: flattenSubmissionErrors,
    };
}
